import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewEncapsulation,
  inject,
  signal,
  WritableSignal
} from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { AuthActionType } from '@App/pages/auth/auth.type';
import { AppSettings } from '@Shared/app.settings';
import { EMPTY, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService, StorageService } from '@Shared/services';
import { CardPackageService } from '@App/modules/cards-packages/services';
import { EmployerPortalService } from '@App/modules/choosen-history/services';
import { AuthService } from '@App/pages/auth/services';
import { LoaderService } from '@Shared/components/loader/loader.service';
import { switchMap, takeUntil } from 'rxjs/operators';
import { UserSession } from '@App/common/models';
import { UserCommonApiService } from '@App/common/services';
import { CommonModule, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import {
  PricingService,
  PricingStep
} from '@App/pages/pricing/pricing.service';
import { PasswordModule } from 'primeng/password';
import { NoLeadingSpaceDirective } from '@App/common/directives';
import { environment } from '@Environments';

declare const google: any;
declare const AppleID: any;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    TranslateModule,
    PasswordModule,
    NoLeadingSpaceDirective,
    NgIf
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class SignInComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() impersonate: boolean;
  @Output() activeTypeChange: EventEmitter<AuthActionType> = new EventEmitter();
  isRedirectedFromCardsPackages: boolean;
  teamToken: string;
  signInForm: UntypedFormGroup = new UntypedFormGroup({
    emailId: new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(AppSettings.EMAIL_PATTERN)
    ]),
    password: new UntypedFormControl('', [Validators.required])
  });
  @Output() outputFormGroup = new EventEmitter<FormGroup<any>>();
  @Input() isHideMainButton: boolean = false;
  private showPasswordSig: WritableSignal<boolean> = signal(true);
  private isLoadingSig: WritableSignal<boolean> = signal(false);
  private isDestroyedSub: Subject<boolean> = new Subject<boolean>();
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userCommonService = inject(UserCommonApiService);
  private storageService = inject(StorageService);
  private cardPackagesService = inject(CardPackageService);
  private employerPortalService = inject(EmployerPortalService);
  private authService = inject(AuthService);
  private loaderService = inject(LoaderService);
  private pricingService = inject(PricingService);
  private notificationService = inject(NotificationService);
  private platformId = inject(PLATFORM_ID);

  get showPassword(): boolean {
    return this.showPasswordSig();
  }

  get isLoading(): boolean {
    return this.isLoadingSig();
  }

  ngOnInit() {
    if (this.impersonate) {
      this.authService.actionParams$
        .pipe(takeUntil(this.isDestroyedSub))
        .subscribe((token) => {
          if (token) {
            this.doImpersonateSignIn(token);
          }
        });
    }
    this.signInForm.valueChanges.subscribe((form) => {
      this.outputFormGroup.emit(this.signInForm);
    });
    this.signInForm.setErrors(null);
  }

  ngAfterViewInit(): void {
    this.preloadGoogleSignIn();
    this.preloadAppleSignIn();
  }

  private preloadGoogleSignIn(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const waitForGoogle = setInterval(() => {
      if (typeof google !== 'undefined' && google?.accounts?.id) {
        clearInterval(waitForGoogle);
        google.accounts.id.initialize({
          client_id: environment.googleClientId,
          callback: (response: { credential: string }) => {
            this.handleGoogleSignIn(response.credential);
          }
        });
      }
    }, 200);
  }

  signInWithGoogle(): void {
    if (
      !isPlatformBrowser(this.platformId) ||
      typeof google === 'undefined' ||
      !google?.accounts?.id
    ) {
      this.notificationService.showNotification(
        'WARNING',
        'Google sign-in is loading, please try again in a moment.'
      );
      return;
    }
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;';
    document.body.appendChild(container);
    google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      type: 'standard'
    });
    setTimeout(() => {
      const btn = container.querySelector(
        '[role="button"], button'
      ) as HTMLElement;
      if (btn) {
        btn.click();
      }
      setTimeout(() => container.remove(), 10000);
    }, 100);
  }

  private preloadAppleSignIn(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (!environment.appleClientId || !environment.appleRedirectUri) {
      return;
    }
    const waitForApple = setInterval(() => {
      if (typeof AppleID !== 'undefined' && AppleID?.auth?.init) {
        clearInterval(waitForApple);
        AppleID.auth.init({
          clientId: environment.appleClientId,
          scope: 'name email',
          redirectURI: environment.appleRedirectUri,
          usePopup: true
        });
      }
    }, 200);
  }

  signInWithApple(): void {
    if (!environment.appleClientId || !environment.appleRedirectUri) {
      this.notificationService.showNotification(
        'WARNING',
        'Apple sign-in is not configured yet.'
      );
      return;
    }
    if (
      !isPlatformBrowser(this.platformId) ||
      typeof AppleID === 'undefined' ||
      !AppleID?.auth?.signIn
    ) {
      this.notificationService.showNotification(
        'WARNING',
        'Apple sign-in is loading, please try again in a moment.'
      );
      return;
    }
    this.isLoadingSig.set(true);
    AppleID.auth
      .signIn()
      .then((response: any) => {
        const json = JSON.stringify({
          authorizationCode: response?.authorization?.code,
          givenName: response?.user?.name?.firstName,
          familyName: response?.user?.name?.lastName,
          email: response?.user?.email,
          identityToken: response?.authorization?.id_token
        });
        const idToken = response?.authorization?.id_token;
        if (!idToken) {
          throw new Error('Missing Apple id token');
        }
        this.handleAppleSignIn(json);
      })
      .catch(() => {
        this.isLoadingSig.set(false);
        this.loaderService.hide();
        this.notificationService.showNotification(
          'ERROR',
          'Apple sign-in failed. Please try again.'
        );
      });
  }

  handleGoogleSignIn(idToken: string): void {
    this.isLoadingSig.set(true);
    this.userCommonService
      .oauthSignIn(idToken, 'Google')
      .pipe(
        takeUntil(this.isDestroyedSub),
        switchMap(({ data }) => {
          this.userCommonService.clearUserDataForSwitch();
          const userSession = new UserSession(data);
          this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
          this.storageService.setItemInCookies(
            AppSettings.TOKEN_KEY,
            userSession.token
          );
          this.userCommonService.hideLinks.next(true);

          const context = this.authService.getCurrentContext();
          if (context?.isFromPricing && context.returnToStep) {
            this.pricingService.goBackAfterAuthClose(context.returnToStep);
          }

          window.scrollTo(0, 0);
          this.isLoadingSig.set(false);
          this.loaderService.hide();

          const inviteToken = sessionStorage.getItem('inviteToken');
          if (inviteToken) {
            return this.userCommonService.acceptInvite(inviteToken);
          }

          this.authService.setAction(null);
          this.checkTeamToken();
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          setTimeout(() =>
            this.notificationService.showNotification(
              'SUCCESS',
              'Your invite has been accepted'
            )
          );
          sessionStorage.removeItem('inviteToken');
          this.authService.setAction(null);
          this.checkTeamToken();
        },
        error: () => {
          this.isLoadingSig.set(false);
          this.loaderService.hide();
          this.notificationService.showNotification(
            'ERROR',
            'Google sign-in failed. Please try again.'
          );
        }
      });
  }

  handleAppleSignIn(idToken): void {
    this.isLoadingSig.set(true);
    this.userCommonService
      .oauthSignIn(idToken, 'Apple')
      .pipe(
        takeUntil(this.isDestroyedSub),
        switchMap(({ data }) => {
          this.userCommonService.clearUserDataForSwitch();
          const userSession = new UserSession(data);
          this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
          this.storageService.setItemInCookies(
            AppSettings.TOKEN_KEY,
            userSession.token
          );
          this.userCommonService.hideLinks.next(true);

          const context = this.authService.getCurrentContext();
          if (context?.isFromPricing && context.returnToStep) {
            this.pricingService.goBackAfterAuthClose(context.returnToStep);
          }

          window.scrollTo(0, 0);
          this.isLoadingSig.set(false);
          this.loaderService.hide();

          const inviteToken = sessionStorage.getItem('inviteToken');
          if (inviteToken) {
            return this.userCommonService.acceptInvite(inviteToken);
          }

          this.authService.setAction(null);
          this.checkTeamToken();
          return EMPTY;
        })
      )
      .subscribe({
        next: () => {
          setTimeout(() =>
            this.notificationService.showNotification(
              'SUCCESS',
              'Your invite has been accepted'
            )
          );
          sessionStorage.removeItem('inviteToken');
          this.authService.setAction(null);
          this.checkTeamToken();
        },
        error: () => {
          this.isLoadingSig.set(false);
          this.loaderService.hide();
          this.notificationService.showNotification(
            'ERROR',
            'Apple sign-in failed. Please try again.'
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.isDestroyedSub.next(true);
    this.isDestroyedSub.complete();
    this.resetForm();
  }

  resetForm(): void {
    this.signInForm.reset();
    this.signInForm.markAsPristine();
    this.signInForm.markAsUntouched();
  }

  togglePassword() {
    const password = document.getElementById('password');
    if (password['type'] === 'password') {
      password['type'] = 'text';
      this.showPasswordSig.set(false);
    } else {
      password['type'] = 'password';
      this.showPasswordSig.set(true);
    }
  }

  doImpersonateSignIn(token) {
    if (!token) {
      return;
    }
    this.isLoadingSig.set(true);
    this.userCommonService
      .impersonate(token?.token || token)
      .pipe(takeUntil(this.isDestroyedSub))
      .subscribe({
        next: (res) => {
          this.userCommonService.clearUserDataForSwitch();
          const userSession = new UserSession(res.data);
          userSession.email = this.signInForm.value.emailId;
          this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
          this.storageService.setItemInCookies(
            AppSettings.TOKEN_KEY,
            userSession.token
          );
          this.authService.setAction(null);
          this.userCommonService.hideLinks.next(true);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {}
          });
          this.isLoadingSig.set(false);
          this.loaderService.hide();
        },
        error: (err) => {
          this.authService.setAction(null);
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {}
          });
          this.isLoadingSig.set(false);
          this.loaderService.hide();
        }
      });
  }

  doSignIn() {
    if (this.signInForm.invalid) {
      this.signInForm.markAllAsTouched();
      this.signInForm.get('emailId').markAsTouched();
      this.signInForm.get('emailId').markAsDirty();
      this.signInForm.get('password').markAsDirty();
      this.signInForm.get('password').markAsTouched();
      return;
    } else {
      this.isLoadingSig.set(true);
      this.userCommonService
        .signIn(this.signInForm.value)
        .pipe(
          takeUntil(this.isDestroyedSub),
          switchMap(({ data }) => {
            this.userCommonService.clearUserDataForSwitch();
            const userSession = new UserSession(data);
            userSession.email = this.signInForm.value.emailId;
            this.storageService.setItem(AppSettings.USER_DETAILS, userSession);
            this.storageService.setItemInCookies(
              AppSettings.TOKEN_KEY,
              userSession.token
            );
            this.userCommonService.hideLinks.next(true);

            // Handle pricing context navigation
            const context = this.authService.getCurrentContext();
            if (context?.isFromPricing && context.returnToStep) {
              this.pricingService.goBackAfterAuthClose(context.returnToStep);
            }

            window.scrollTo(0, 0);
            this.isLoadingSig.set(false);
            this.loaderService.hide();

            const inviteToken = sessionStorage.getItem('inviteToken');

            if (inviteToken) {
              return this.userCommonService.acceptInvite(inviteToken);
            }

            this.authService.setAction(null);
            this.checkTeamToken();
            return EMPTY;
          })
        )
        .subscribe({
          next: () => {
            setTimeout(() =>
              this.notificationService.showNotification(
                'SUCCESS',
                'Your invite has been accepted'
              )
            );
            sessionStorage.removeItem('inviteToken');
            this.authService.setAction(null);
            this.checkTeamToken();
          },
          error: () => {
            sessionStorage.removeItem('inviteToken');
            this.authService.setAction(null);
            this.checkTeamToken();
          }
        });
    }
  }

  changeActionType(type: AuthActionType) {
    this.activeTypeChange.emit(type);
  }

  private checkTeamToken() {
    if (this.teamToken) {
      this.employerPortalService
        .joinTeamByInvite(+this.teamToken)
        .pipe(takeUntil(this.isDestroyedSub))
        .subscribe((r) => {
          if (r.data.businessId) {
            this.storageService.setSession(
              AppSettings.DRAFT_BUSINESS_ID,
              r.data.businessId
            );
            this.cardPackagesService.selectedType = r.data.businessType;
            this.router.navigate([
              '/create/business?businessId=' + r.data.businessId
            ]);
          }
        });
    } else if (
      this.router.url.includes('/pricing') ||
      this.router.url.includes('/affiliate')
    ) {
      return;
    } else if (this.route.snapshot.queryParams['serviceUrl']) {
      this.router.navigate([this.route.snapshot.queryParams['serviceUrl']]);
    } else {
      this.router.navigate(['']);
    }
  }
}
